import React, { useState } from 'react';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { useLang, tx } from '../context/LanguageContext';
import { submitCallbackB2B } from '../lib/configurator';

const API_BASE = process.env.REACT_APP_PORTAIL_API_URL || 'https://support.meteris.fr';
const SIRENE_LOOKUP_URL = `${API_BASE}/api/public/it-bookings/sirene-lookup`;

export default function CallbackB2BForm({ contextTag, treePath }) {
  const { lang } = useLang();
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    siret: '', society_in_creation: false, society_name_fallback: '',
    rgpd_consent: false, honeypot: '', free_text: '',
  });
  const [sireneInfo, setSireneInfo] = useState(null);
  const [loadingSirene, setLoadingSirene] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const lookupSirene = async (siret) => {
    if (!/^\d{14}$/.test(siret)) {
      setSireneInfo(null);
      return;
    }
    setLoadingSirene(true);
    try {
      const r = await fetch(SIRENE_LOOKUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siret }),
      });
      if (r.ok) {
        const data = await r.json();
        if (data.found) {
          setSireneInfo(data);
        } else {
          setSireneInfo(null);
        }
      }
    } catch {
      setSireneInfo(null);
    } finally {
      setLoadingSirene(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        siret: form.society_in_creation ? null : form.siret,
        society_name_fallback: form.society_in_creation ? form.society_name_fallback : null,
        tree_path: treePath,
        context_tag: contextTag,
      };
      await submitCallbackB2B(payload);
      setSuccess(true);
    } catch (err) {
      setError(err.message || tx(lang, 'Une erreur s\'est produite.', 'An error occurred.', {
        es: 'Se ha producido un error.',
        de: 'Ein Fehler ist aufgetreten.',
        it: 'Si è verificato un errore.',
        pt: 'Ocorreu um erro.',
      }));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">
          {tx(lang, 'Demande reçue', 'Request received', {
            es: 'Solicitud recibida',
            de: 'Anfrage erhalten',
            it: 'Richiesta ricevuta',
            pt: 'Pedido recebido',
          })}
        </h3>
        <p className="text-slate-300">
          {contextTag === 'urgence'
            ? tx(lang,
                "Pour une urgence, n'hésitez pas à appeler le 01 83 62 33 27 en parallèle.",
                "For an emergency, don't hesitate to call 01 83 62 33 27 in parallel.",
                {
                  es: 'Para una emergencia, no dude en llamar al 01 83 62 33 27 en paralelo.',
                  de: 'Im Notfall zögern Sie nicht, parallel die 01 83 62 33 27 anzurufen.',
                  it: 'Per un\'emergenza, non esitate a chiamare il 01 83 62 33 27 in parallelo.',
                  pt: 'Para uma emergência, não hesite em ligar para 01 83 62 33 27 em paralelo.',
                })
            : tx(lang,
                'Nous vous rappelons sous 4 h ouvrées (lundi-vendredi 9 h – 12 h et 14 h – 17 h).',
                'We will call you back within 4 business hours (Monday-Friday 9 AM – 12 PM and 2 PM – 5 PM).',
                {
                  es: 'Le devolveremos la llamada en 4 horas laborables (lunes a viernes 9:00 - 12:00 y 14:00 - 17:00).',
                  de: 'Wir rufen Sie innerhalb von 4 Arbeitsstunden zurück (Montag bis Freitag 9 - 12 Uhr und 14 - 17 Uhr).',
                  it: 'Vi richiameremo entro 4 ore lavorative (lunedì-venerdì 9:00 - 12:00 e 14:00 - 17:00).',
                  pt: 'Ligaremos de volta em 4 horas úteis (segunda a sexta 9h00 - 12h00 e 14h00 - 17h00).',
                })}
        </p>
      </div>
    );
  }

  const lbl = {
    firstname: tx(lang, 'Prénom', 'First name', { es: 'Nombre', de: 'Vorname', it: 'Nome', pt: 'Nome' }),
    lastname: tx(lang, 'Nom', 'Last name', { es: 'Apellido', de: 'Nachname', it: 'Cognome', pt: 'Apelido' }),
    email: tx(lang, 'Email professionnel', 'Business email', { es: 'Email profesional', de: 'Geschäftliche E-Mail', it: 'Email professionale', pt: 'Email profissional' }),
    phone: tx(lang, 'Téléphone', 'Phone', { es: 'Teléfono', de: 'Telefon', it: 'Telefono', pt: 'Telefone' }),
    society_creating: tx(lang, 'Société en cours de création (pas encore de SIRET)', 'Company being created (no SIRET yet)', {
      es: 'Empresa en creación (aún sin SIRET)',
      de: 'Unternehmen in Gründung (noch keine SIRET)',
      it: 'Società in costituzione (ancora senza SIRET)',
      pt: 'Empresa em criação (ainda sem SIRET)',
    }),
    society_name: tx(lang, 'Nom de la société (en cours)', 'Company name (in progress)', {
      es: 'Nombre de la empresa (en curso)',
      de: 'Firmenname (in Bearbeitung)',
      it: 'Nome della società (in corso)',
      pt: 'Nome da empresa (em curso)',
    }),
    siret: tx(lang, 'SIRET (14 chiffres)', 'SIRET (14 digits)', {
      es: 'SIRET (14 dígitos)',
      de: 'SIRET (14 Ziffern)',
      it: 'SIRET (14 cifre)',
      pt: 'SIRET (14 dígitos)',
    }),
    checking_sirene: tx(lang, 'Vérification SIRENE…', 'Checking SIRENE…', {
      es: 'Comprobación SIRENE…',
      de: 'SIRENE-Prüfung…',
      it: 'Verifica SIRENE…',
      pt: 'A verificar SIRENE…',
    }),
    free_text: tx(lang, 'Précisions (optionnel)', 'Details (optional)', {
      es: 'Detalles (opcional)',
      de: 'Details (optional)',
      it: 'Dettagli (opzionale)',
      pt: 'Detalhes (opcional)',
    }),
    rgpd_consent_intro: tx(lang,
      'J\'accepte que mes données soient utilisées pour traiter ma demande',
      'I agree that my data will be used to process my request',
      {
        es: 'Acepto que mis datos sean utilizados para tratar mi solicitud',
        de: 'Ich stimme zu, dass meine Daten zur Bearbeitung meiner Anfrage verwendet werden',
        it: 'Accetto che i miei dati siano utilizzati per gestire la mia richiesta',
        pt: 'Aceito que os meus dados sejam utilizados para tratar o meu pedido',
      }),
    rgpd_link: tx(lang, 'politique de confidentialité', 'privacy policy', {
      es: 'política de privacidad',
      de: 'Datenschutzerklärung',
      it: 'politica sulla privacy',
      pt: 'política de privacidade',
    }),
    submitting: tx(lang, 'Envoi…', 'Sending…', { es: 'Enviando…', de: 'Senden…', it: 'Invio…', pt: 'A enviar…' }),
    submit: tx(lang, 'Demander un rappel', 'Request a callback', {
      es: 'Solicitar una llamada',
      de: 'Rückruf anfordern',
      it: 'Richiedi una richiamata',
      pt: 'Solicitar uma chamada',
    }),
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-[#15171E]/80 border border-white/5 rounded-2xl p-6">
      {/* Honeypot — invisible aux humains, piège les bots */}
      <input
        type="text"
        value={form.honeypot}
        onChange={(e) => update('honeypot', e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input label={lbl.firstname} value={form.first_name} onChange={(v) => update('first_name', v)} required />
        <Input label={lbl.lastname} value={form.last_name} onChange={(v) => update('last_name', v)} required />
        <Input label={lbl.email} type="email" value={form.email} onChange={(v) => update('email', v)} required />
        <Input label={lbl.phone} type="tel" value={form.phone} onChange={(v) => update('phone', v)} required />
      </div>

      {/* SIRET / société en cours de création */}
      <div>
        <label className="flex items-center gap-2 text-sm text-slate-300 mb-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.society_in_creation}
            onChange={(e) => update('society_in_creation', e.target.checked)}
          />
          {lbl.society_creating}
        </label>
        {form.society_in_creation ? (
          <Input
            label={lbl.society_name}
            value={form.society_name_fallback}
            onChange={(v) => update('society_name_fallback', v)}
            required
          />
        ) : (
          <>
            <Input
              label={lbl.siret}
              value={form.siret}
              onChange={(v) => { update('siret', v); lookupSirene(v); }}
              maxLength={14}
              required
            />
            {loadingSirene && (
              <p className="text-xs text-slate-500 mt-1">
                <Loader2 className="inline w-3 h-3 animate-spin" /> {lbl.checking_sirene}
              </p>
            )}
            {sireneInfo && (
              <p className="text-xs text-emerald-400 mt-1">
                ✓ {sireneInfo.raison_sociale} — {sireneInfo.ville} ({sireneInfo.code_postal})
              </p>
            )}
          </>
        )}
      </div>

      <Textarea
        label={lbl.free_text}
        value={form.free_text}
        onChange={(v) => update('free_text', v)}
        rows={3}
        maxLength={2000}
      />

      <label className="flex items-start gap-2 text-sm text-slate-300 cursor-pointer">
        <input
          type="checkbox"
          checked={form.rgpd_consent}
          onChange={(e) => update('rgpd_consent', e.target.checked)}
          required
        />
        <span>
          {lbl.rgpd_consent_intro}
          {' '}(<a href="/politique-confidentialite" className="text-[#0099D6] underline">{lbl.rgpd_link}</a>).
        </span>
      </label>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-[#0099D6] hover:bg-[#0284C7] text-white font-semibold py-3 px-6 rounded-lg disabled:opacity-50 transition-all"
      >
        {submitting
          ? <><Loader2 className="inline w-4 h-4 animate-spin mr-2" />{lbl.submitting}</>
          : lbl.submit}
      </button>
    </form>
  );
}

function Input({ label, value, onChange, type = 'text', required = false, maxLength }) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">{label}{required && ' *'}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        maxLength={maxLength}
        className="w-full bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0099D6]/50 transition-colors"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, rows, maxLength }) {
  return (
    <label className="block">
      <span className="block text-sm text-slate-300 mb-1">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        maxLength={maxLength}
        className="w-full bg-[#0B0C10] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-[#0099D6]/50 transition-colors resize-y"
      />
    </label>
  );
}
